import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Printer,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Home,
  Gift,
  Lamp,
  Gem,
  Key,
  Gamepad2,
  Palette,
  Box,
  Flower2,
  User,
  Building,
  Ruler,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';

// Product type options for 3D printing
const PRODUCT_TYPES = [
  { value: 'action_figure', label: 'Action Figure / Statuetta', icon: Gamepad2 },
  { value: 'lampada', label: 'Lampada / Illuminazione', icon: Lamp },
  { value: 'decorazione', label: 'Decorazione Casa', icon: Home },
  { value: 'gioiello', label: 'Gioiello / Bijoux', icon: Gem },
  { value: 'portachiavi', label: 'Portachiavi / Magneti', icon: Key },
  { value: 'vaso_fioriera', label: 'Vaso / Fioriera', icon: Flower2 },
  { value: 'scultura', label: 'Scultura Artistica', icon: Sparkles },
  { value: 'modellino', label: 'Modellino / Miniatura', icon: Box },
  { value: 'gadget', label: 'Gadget Personalizzato', icon: Zap },
  { value: 'prototipo', label: 'Prototipo / Funzionale', icon: Layers },
  { value: 'altro', label: 'Altro', icon: Printer },
];

// Material options
const MATERIAL_OPTIONS = [
  { value: 'pla', label: 'PLA (biodegradabile)', icon: Flower2 },
  { value: 'petg', label: 'PETG (resistente)', icon: Shield },
  { value: 'abs', label: 'ABS (meccanico)', icon: Zap },
  { value: 'resina', label: 'Resina (alta definizione)', icon: Gem },
  { value: 'tpu', label: 'TPU (flessibile)', icon: Sparkles },
  { value: 'nylon', label: 'Nylon (professionale)', icon: Layers },
  { value: 'legno', label: 'PLA Legno (effetto naturale)', icon: Home },
  { value: 'metallo', label: 'Filamento metallico', icon: Star },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: MessageSquare },
];

// Finish options
const FINISH_OPTIONS = [
  { value: 'grezzo', label: 'Grezzo (stampa base)', icon: Printer },
  { value: 'levigato', label: 'Levigato / Carteggiato', icon: Sparkles },
  { value: 'verniciato', label: 'Verniciato (colore unico)', icon: Palette },
  { value: 'dipinto', label: 'Dipinto a mano (dettagliato)', icon: Palette },
  { value: 'cromato', label: 'Cromato / Metallizzato', icon: Star },
  { value: 'trasparente', label: 'Trasparente / Lucido', icon: Gem },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: MessageSquare },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'mini', label: 'Mini (fino a 5 cm)', icon: Ruler },
  { value: 'piccolo', label: 'Piccolo (5-10 cm)', icon: Ruler },
  { value: 'medio', label: 'Medio (10-20 cm)', icon: Ruler },
  { value: 'grande', label: 'Grande (20-30 cm)', icon: Ruler },
  { value: 'xl', label: 'Extra Large (30-50 cm)', icon: Ruler },
  { value: 'xxl', label: 'Monumentale (oltre 50 cm)', icon: Ruler },
  { value: 'personalizzato', label: 'Misure personalizzate', icon: Sparkles },
];

// Purpose options
const PURPOSE_OPTIONS = [
  { value: 'casa', label: 'Per arredare casa', icon: Home },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'collezione', label: 'Per collezione', icon: Star },
  { value: 'lavoro', label: 'Per lavoro / Ufficio', icon: Building },
  { value: 'evento', label: 'Per evento / Fiera', icon: Users },
  { value: 'prototipo', label: 'Prototipo prodotto', icon: Layers },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '10-30', label: '€10 - €30', description: 'Oggetto piccolo' },
  { value: '30-60', label: '€30 - €60', description: 'Media complessità' },
  { value: '60-100', label: '€60 - €100', description: 'Dettagliato' },
  { value: '100-200', label: '€100 - €200', description: 'Grande formato' },
  { value: '200-400', label: '€200 - €400', description: 'Professionale' },
  { value: '400-800', label: '€400 - €800', description: 'Progetto complesso' },
  { value: '800+', label: '€800+', description: 'Multi-pezzo / Serie' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '3days', label: '3 Giorni', description: 'Urgente' },
  { value: '1week', label: '1 Settimana', description: 'Prioritario' },
  { value: '2weeks', label: '2 Settimane', description: 'Standard' },
  { value: '1month', label: '1 Mese', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example 3D prints for showcase
const PRINT_EXAMPLES = [
  {
    idea: "Action figure di personaggio anime preferito",
    result: "Statuetta 25cm in resina, dipinta a mano con base personalizzata e illuminazione LED",
    image: "https://images.pexels.com/photos/16075339/pexels-photo-16075339.jpeg?w=400&h=300&fit=crop",
    category: "Action Figure"
  },
  {
    idea: "Lampada luna per cameretta bambino",
    result: "Lampada sferica 20cm con texture lunare realistica, LED dimmerabile e telecomando",
    image: "https://images.unsplash.com/photo-1549858670-d4fb4a5e0e18?w=400&h=300&fit=crop",
    category: "Lampada"
  },
  {
    idea: "Vaso geometrico moderno per piante grasse",
    result: "Set 3 vasi in PLA ecologico, design sfaccettato con foro drenaggio, colori pastello",
    image: "https://images.unsplash.com/photo-1703221561813-cdaa308cf9e7?w=400&h=300&fit=crop",
    category: "Vaso"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho fatto stampare una lampada personalizzata con il logo della mia azienda. Qualità incredibile e tempi rispettati. Ora ne ordino per tutti i clienti!",
    author: "Francesco B.",
    location: "Milano"
  },
  {
    text: "Colleziono action figure e quella realizzata su misura del mio personaggio preferito è la più bella della mia collezione. Dettagli perfetti!",
    author: "Chiara V.",
    location: "Roma"
  },
  {
    text: "Cercavo un prototipo funzionale per la mia startup. L'artigiano ha capito subito le specifiche tecniche e il risultato è stato perfetto per i test.",
    author: "Alessandro M.",
    location: "Torino"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Che differenza c'è tra stampa FDM e resina?",
    answer: "La stampa FDM (filamento) è ideale per oggetti grandi, funzionali e resistenti. La stampa in resina offre dettagli superiori ed è perfetta per miniature, gioielli e oggetti con superfici lisce. L'artigiano ti consiglierà la tecnologia migliore per il tuo progetto."
  },
  {
    question: "Posso inviare un file 3D già pronto?",
    answer: "Certamente! Se hai già un file STL, OBJ o 3MF puoi allegarlo alla richiesta. Se invece hai solo un'idea o un'immagine, l'artigiano può creare il modello 3D per te."
  },
  {
    question: "I colori sono personalizzabili?",
    answer: "Sì, puoi richiedere qualsiasi colore. Per oggetti monocromatici si usa filamento colorato, per decorazioni elaborate si applica pittura a mano post-stampa."
  },
  {
    question: "Quanto tempo ci vuole per una stampa 3D?",
    answer: "Dipende dalle dimensioni e dalla complessità: un oggetto piccolo può essere pronto in 2-3 giorni, progetti grandi o con finitura elaborata richiedono 1-2 settimane."
  },
  {
    question: "Posso richiedere più copie dello stesso oggetto?",
    answer: "Assolutamente! Molti clienti ordinano serie per regali, gadget aziendali o merchandising. Indica la quantità nella descrizione per ricevere preventivi appropriati."
  }
];

export default function Stampa3DRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    product_type: '',
    material: '',
    finish: '',
    size: '',
    purpose: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Stampa 3D Su Misura | Creazioni Personalizzate | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 file');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      // Allow images and 3D files
      const allowedTypes = ['image/', 'model/', '.stl', '.obj', '.3mf'];
      const isAllowed = allowedTypes.some(type => 
        file.type.startsWith(type) || file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      
      if (!isAllowed && !file.type.startsWith('image/')) {
        toast.error('Formato non supportato. Usa immagini o file 3D (STL, OBJ, 3MF)');
        continue;
      }
      
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File troppo grande (max 50MB)');
        continue;
      }
      
      try {
        const fileName = `3dprint-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi cosa vorresti stampare');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_3dprint_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const productLabel = PRODUCT_TYPES.find(t => t.value === formData.product_type)?.label || '';
      const materialLabel = MATERIAL_OPTIONS.find(t => t.value === formData.material)?.label || '';
      const finishLabel = FINISH_OPTIONS.find(t => t.value === formData.finish)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(t => t.value === formData.size)?.label || '';
      const purposeLabel = PURPOSE_OPTIONS.find(p => p.value === formData.purpose)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (productLabel) details.push(`🖨️  Tipo: ${productLabel}`);
      if (materialLabel) details.push(`🧱  Materiale: ${materialLabel}`);
      if (finishLabel) details.push(`✨  Finitura: ${finishLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (purposeLabel) details.push(`🎯  Utilizzo: ${purposeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${productLabel} - Stampa 3D`.trim() || 'Stampa 3D personalizzata',
        description: fullDescription,
        category: 'Stampa 3D',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai maker.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('print-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Stampa 3D Su Misura | Creazioni Personalizzate Artigianali | Rarifex</title>
        <meta name="description" content="Stampa 3D su misura: action figure, lampade, decorazioni, prototipi. Maker italiani realizzano le tue idee in PLA, resina, PETG. Gratuito e senza impegno." />
        <meta name="keywords" content="stampa 3d personalizzata, stampa 3d su misura, action figure 3d, lampada 3d, maker italiano, prototipazione, stampa resina" />
        <link rel="canonical" href="https://www.rarifex.com/stampa-3d-su-misura" />
        <meta property="og:title" content="Stampa 3D Su Misura | Creazioni Personalizzate | Rarifex" />
        <meta property="og:description" content="Stampa 3D su misura realizzata da maker italiani. Action figure, lampade, decorazioni e prototipi. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/stampa-3d-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-cyan-50/50 via-white to-blue-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Printer className="w-4 h-4" />
                    Stampa 3D Personalizzata
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Le tue idee{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">
                      prendono forma
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maker italiani trasformano la tua immaginazione in oggetti reali.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Action figure, lampade, decorazioni, prototipi: qualsiasi cosa tu possa immaginare.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Maker verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-cyan-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="print-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Action figure del mio personaggio preferito"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="print-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi cosa vorresti stampare *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Cosa vorresti? Hai riferimenti o file 3D? Dettagli importanti?"
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="print-description-textarea"
                        />
                      </div>
                      
                      {/* Product Type & Material */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo</Label>
                          <select
                            value={formData.product_type}
                            onChange={(e) => handleInputChange('product_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="print-type-select"
                          >
                            <option value="">Seleziona tipo...</option>
                            {PRODUCT_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Materiale</Label>
                          <select
                            value={formData.material}
                            onChange={(e) => handleInputChange('material', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="print-material-select"
                          >
                            <option value="">Seleziona...</option>
                            {MATERIAL_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Size & Finish */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Dimensioni</Label>
                          <select
                            value={formData.size}
                            onChange={(e) => handleInputChange('size', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="print-size-select"
                          >
                            <option value="">Seleziona...</option>
                            {SIZE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Finitura</Label>
                          <select
                            value={formData.finish}
                            onChange={(e) => handleInputChange('finish', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="print-finish-select"
                          >
                            <option value="">Seleziona...</option>
                            {FINISH_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Purpose & Budget */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Utilizzo</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="print-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {PURPOSE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="print-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          data-testid="print-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* File Upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          File o immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-cyan-400 hover:bg-cyan-50/50 transition-colors">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica immagini o file 3D (max 5)'}
                            </span>
                            <input
                              type="file"
                              accept="image/*,.stl,.obj,.3mf"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                          <p className="text-xs text-slate-400 mt-1">
                            Accettiamo immagini e file 3D (STL, OBJ, 3MF)
                          </p>
                        </div>
                        
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                {url.match(/\.(stl|obj|3mf)$/i) ? (
                                  <div className="w-16 h-16 bg-cyan-100 rounded-lg flex items-center justify-center">
                                    <Layers className="w-6 h-6 text-cyan-600" />
                                  </div>
                                ) : (
                                  <img
                                    src={url}
                                    alt={`Riferimento ${index + 1}`}
                                    className="w-16 h-16 object-cover rounded-lg"
                                  />
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="print-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-500">
                        Riceverai proposte da maker verificati entro 24-48 ore
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                In 4 semplici passi avrai il tuo oggetto stampato in 3D
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci cosa vorresti creare' },
                { icon: Users, title: 'Ricevi', desc: 'Proposte da maker esperti' },
                { icon: Star, title: 'Scegli', desc: 'Il maker e il preventivo ideale' },
                { icon: Printer, title: 'Ricevi', desc: 'Il tuo oggetto prende vita' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT CAN BE PRINTED */}
        <section className="py-16 bg-gradient-to-b from-white to-cyan-50/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Puoi Stampare
              </h2>
              <p className="text-lg text-slate-600">
                I nostri maker realizzano qualsiasi tipo di oggetto
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { icon: Gamepad2, label: 'Action Figure', color: 'bg-purple-100 text-purple-600' },
                { icon: Lamp, label: 'Lampade', color: 'bg-yellow-100 text-yellow-600' },
                { icon: Home, label: 'Decorazioni', color: 'bg-green-100 text-green-600' },
                { icon: Gem, label: 'Gioielli', color: 'bg-pink-100 text-pink-600' },
                { icon: Key, label: 'Portachiavi', color: 'bg-orange-100 text-orange-600' },
                { icon: Flower2, label: 'Vasi', color: 'bg-emerald-100 text-emerald-600' },
                { icon: Layers, label: 'Prototipi', color: 'bg-blue-100 text-blue-600' },
                { icon: Box, label: 'Gadget', color: 'bg-cyan-100 text-cyan-600' },
              ].map((item, index) => (
                <div key={index} className={`${item.color} rounded-xl p-4 text-center`}>
                  <item.icon className="w-8 h-8 mx-auto mb-2" />
                  <p className="font-medium text-sm">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Creazioni Realizzate
              </h2>
              <p className="text-lg text-slate-600">
                Dalla richiesta all'oggetto finito
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {PRINT_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={example.image}
                    alt={example.result}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <span className="inline-block px-3 py-1 bg-cyan-100 text-cyan-800 text-xs font-medium rounded-full mb-3">
                      {example.category}
                    </span>
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Richiesta:</strong> {example.idea}
                    </p>
                    <p className="text-sm text-slate-800">
                      <strong>Risultato:</strong> {example.result}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-gradient-to-b from-cyan-50/30 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Dicono i Clienti
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-cyan-400 text-cyan-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.location}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-5 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 bg-gradient-to-r from-cyan-600 to-blue-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto a dare vita alle tue idee?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Richiedi preventivi gratuiti dai migliori maker italiani
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-cyan-600 hover:bg-cyan-50 font-semibold shadow-xl px-8 py-6 text-lg"
              data-testid="print-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inizia Ora - È Gratis
            </Button>
          </div>
        </section>
        
        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Accedi per continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta e ricevere proposte dai maker, accedi o crea un account.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                  onClick={() => navigate('/login?redirect=/stampa-3d-su-misura')}
                >
                  Accedi
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
